import { useState } from 'react';
import { CLASSE_SURVOL, useListeClavier } from '../hooks/useListeClavier';
import type { Activite } from '../types';

/**
 * Choix d'une activite, a la saisie.
 *
 * Meme geste que le choix d'un membre du personnel : on tape, la liste
 * se filtre. A vingt-trois activites — et davantage le jour ou l'ecole
 * en ouvrira —, derouler devient plus long que taper « pia ».
 *
 * La valeur retenue est un IDENTIFIANT, jamais le texte saisi : deux
 * activites peuvent porter des libelles proches d'une annee sur l'autre,
 * et rien ne doit se choisir par ressemblance.
 */
export function ChoixActivite({
  activites,
  valeur,
  onChanger,
  placeholder = 'Taper une activité…',
  desactive = false,
}: {
  activites: Activite[];
  valeur: string;
  onChanger: (id: string) => void;
  placeholder?: string;
  desactive?: boolean;
}) {
  const [recherche, setRecherche] = useState('');
  const [ouvert, setOuvert] = useState(false);

  const choisie = activites.find((a) => a.id === valeur) ?? null;

  // Seules les activites ACTIVES se proposent : une activite desactivee
  // reste rattachee a ses inscriptions passees, mais n'en recoit plus.
  const candidates = activites
    .filter((a) => a.actif)
    .filter((a) => {
      const terme = recherche.trim().toLowerCase();
      if (!terme) return true;
      return `${a.libelle} ${a.type}`.toLowerCase().includes(terme);
    })
    .slice(0, 12);

  const choisir = (activite: Activite) => {
    onChanger(activite.id);
    setRecherche('');
    setOuvert(false);
  };
  const clavier = useListeClavier(candidates, choisir);

  // Une activite choisie s'affiche comme une pastille : garder le texte
  // tape apres coup laisserait croire qu'il vaut selection.
  if (choisie && !ouvert) {
    return (
      <div className="flex items-center gap-2">
        <span className="champ flex flex-1 items-center justify-between gap-2">
          <span className="truncate">
            {choisie.libelle} <span className="text-encre-500">({choisie.type})</span>
          </span>
          {!desactive && (
            <button
              type="button"
              className="shrink-0 text-sm text-encre-500 underline"
              onClick={() => onChanger('')}
            >
              Retirer
            </button>
          )}
        </span>
        {!desactive && (
          <button
            type="button"
            className="shrink-0 text-sm text-encre-500 underline"
            onClick={() => {
              setRecherche('');
              setOuvert(true);
            }}
          >
            Changer
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="relative">
      <input
        className="champ"
        value={recherche}
        placeholder={placeholder}
        disabled={desactive}
        onChange={(evenement) => {
          setRecherche(evenement.target.value);
          setOuvert(true);
        }}
        onFocus={() => setOuvert(true)}
        onKeyDown={clavier.auClavier}
        // Le clic sur une option doit preceder la fermeture, sinon il ne
        // l'atteint jamais.
        onBlur={() => window.setTimeout(() => setOuvert(false), 150)}
      />

      {ouvert && (
        <ul
          ref={clavier.liste}
          className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-encre-200 bg-white shadow-lg"
        >
          {candidates.length === 0 ? (
            <li className="px-3 py-2 text-sm text-encre-400">Aucune activité ne correspond.</li>
          ) : (
            candidates.map((activite, rang) => (
              <li key={activite.id} data-index={rang}>
                <button
                  type="button"
                  className={`flex w-full items-baseline gap-2 px-3 py-2 text-left text-sm ${
                    rang === clavier.index ? CLASSE_SURVOL : ''
                  }`}
                  onMouseEnter={() => clavier.survoler(rang)}
                  onClick={() => choisir(activite)}
                >
                  <span>{activite.libelle}</span>
                  <span className="text-xs text-encre-500">{activite.type}</span>
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
